import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useMount } from "react-use";
import { isNil } from "lodash";
import { ScheduleTime } from "../../shared/db";
import {
  ScheduleFormValidationErrorMessages,
  ScheduleFormValidator,
  ScheduleTimeHelper,
} from "../../model/schedule";
import { useSelectTheme } from "../../store";
import {
  DefaultTaskScheduleSettingsTextStyle,
  TaskScheduleSettingsActionButton,
  TaskScheduleSettingsDayIntervalInput,
  TaskScheduleSettingsTimeInput,
} from "./task_schedule_settings_input";
import { maybeSoftTry, RIF } from "../../lib";
import { registerName } from "../../lib/form";

interface TaskScheduleSettingsRecurringFixedScheduleForm {
  startTime: string;
  startTimeHours: number;
  startTimeMinutes: number;
  endTime: string;
  endTimeDays?: number;
  endTimeHours: number;
  endTimeMinutes: number;
}

type TaskScheduleSettingsRecurringFixedScheduleFormOutput = Omit<
  TaskScheduleSettingsRecurringFixedScheduleForm,
  "startTimeHours" | "startTimeMinutes" | "endTimeDays" | "endTimeHours" | "endTimeMinutes"
>;

export interface TaskScheduleSettingsRecurringFixedScheduleProps {
  values: Partial<TaskScheduleSettingsRecurringFixedScheduleFormOutput>;
  onValidated: (form: TaskScheduleSettingsRecurringFixedScheduleFormOutput) => void;
  onError: () => void;
}

export const TaskScheduleSettingsRecurringFixedSchedule = (
  props: TaskScheduleSettingsRecurringFixedScheduleProps,
) => {
  const { values, onValidated, onError } = props;

  const { color, fontSize, fontWeight } = useSelectTheme();

  const textStyle = DefaultTaskScheduleSettingsTextStyle({ color, fontSize, fontWeight });

  const defaultStartTime = values.startTime ?? "08:00";
  const defaultStartTimeParsed = maybeSoftTry(() => ScheduleTime.parse(defaultStartTime));

  const defaultEndTime = values.endTime ?? "17:00";
  const defaultEndTimeWithDaysParsed = maybeSoftTry(() =>
    ScheduleTimeHelper.moreThanOneDayScheduleTimeToDayAndScheduleTime(
      ScheduleTime.parse(defaultEndTime, { mayBeMoreThanOneDay: true }),
    ),
  );
  const defaultEndTimeParsed = defaultEndTimeWithDaysParsed?.time;
  const defaultEndTimeDays = defaultEndTimeWithDaysParsed?.days;

  const {
    register,
    control,
    formState: { isValidating, isValid, errors },
    trigger,
    getValues,
    setValue,
    watch,
  } = useForm<TaskScheduleSettingsRecurringFixedScheduleForm>({
    mode: "onChange",
    defaultValues: {
      startTime: defaultStartTime,
      startTimeHours: defaultStartTimeParsed?.hours ?? 8,
      startTimeMinutes: defaultStartTimeParsed?.minutes ?? 0,
      endTime: defaultEndTime,
      endTimeDays: defaultEndTimeDays,
      endTimeHours: defaultEndTimeParsed?.hours ?? 17,
      endTimeMinutes: defaultEndTimeParsed?.minutes ?? 0,
    },
  });

  const [cachedEndTimeDays, setCachedEndTimeDays] = useState(1);

  const [showingMultipleDayOptions, setShowingMultipleDayOptions] = useState(
    Boolean(defaultEndTimeWithDaysParsed?.days),
  );
  const [latestErrorMessage, setLatestErrorMessage] = useState<string | null>(null);

  const endTimeDays = watch("endTimeDays");

  useEffect(() => {
    setValue("endTimeDays", endTimeDays);
    const endTimeHours = getValues("endTimeHours");
    const endTimeMinutes = getValues("endTimeMinutes");

    // bug here
    if (!isNil(endTimeHours) && !isNil(endTimeMinutes)) {
      setValue(
        "endTime",
        ScheduleTimeHelper.format(endTimeHours + (endTimeDays ?? 0) * 24, endTimeMinutes),
      );
    }
    trigger(["endTimeDays", "endTimeHours", "endTimeMinutes", "endTime", "startTime"]);
  }, [endTimeDays]);

  useEffect(() => {
    for (const error of Object.values(errors)) {
      if (error?.message) {
        onError();
        setLatestErrorMessage(error.message);
        return;
      }
    }
    setLatestErrorMessage(null);
  }, [
    errors.startTime,
    errors.startTimeHours,
    errors.startTimeMinutes,
    errors.endTime,
    errors.endTimeHours,
    errors.endTimeMinutes,
  ]);

  useMount(() => {
    trigger();
  });

  useEffect(() => {
    if (isValid && !isValidating) {
      const form = getValues();
      onValidated(form);
    }
  }, [isValid, isValidating]);

  return (
    <div css={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div>
        <span css={{ ...textStyle }}>The task will appear at</span>
        <input
          type="hidden"
          {...register("startTime", {
            required: {
              value: true,
              message: ScheduleFormValidationErrorMessages.required("Start Time"),
            },
            pattern: {
              value: ScheduleTime.REGEX,
              message: ScheduleFormValidationErrorMessages.timeInvalid("Start Time"),
            },
            validate: {
              validTimeInterval: (value) => {
                const { success } = ScheduleFormValidator.validateTimeInterval({
                  startTime: value,
                  endTime: getValues("endTime"),
                  endTimeParseOptions: {
                    mayBeMoreThanOneDay: true,
                  },
                });
                return success
                  ? true
                  : ScheduleFormValidationErrorMessages.shouldBeLessThan("Start Time", "End Time");
              },
            },
            deps: ["endTime"],
          })}
        />
        <TaskScheduleSettingsTimeInput
          control={control}
          hoursName={"startTimeHours"}
          minutesName={"startTimeMinutes"}
          failedValidation={!!errors.startTimeHours || !!errors.startTimeMinutes}
          hoursRules={{
            required: {
              value: true,
              message: ScheduleFormValidationErrorMessages.required("Start Time Hours"),
            },
            min: {
              value: 0,
              message: ScheduleFormValidationErrorMessages.hourInvalid("Start Time Hours"),
            },
            max: {
              value: 23,
              message: ScheduleFormValidationErrorMessages.hourInvalid("Start Time Hours"),
            },
            onChange: (e) => {
              const startTimeHours = parseInt(e.target.value);
              setValue("startTimeHours", startTimeHours);
              const startTimeMinutes = getValues("startTimeMinutes");
              if (!isNil(startTimeMinutes)) {
                setValue("startTime", ScheduleTimeHelper.format(startTimeHours, startTimeMinutes));
              }
              trigger(["startTimeHours", "startTimeMinutes", "startTime", "endTime"]);
            },
          }}
          minutesRules={{
            required: {
              value: true,
              message: ScheduleFormValidationErrorMessages.required("Start Time Minutes"),
            },
            min: {
              value: 0,
              message: ScheduleFormValidationErrorMessages.minuteInvalid("Start Time Minutes"),
            },
            max: {
              value: 59,
              message: ScheduleFormValidationErrorMessages.minuteInvalid("Start Time Minutes"),
            },
            onChange: (e) => {
              const startTimeMinutes = parseInt(e.target.value);
              setValue("startTimeMinutes", startTimeMinutes);
              const startTimeHours = getValues("startTimeHours");
              if (!isNil(startTimeHours)) {
                setValue("startTime", ScheduleTimeHelper.format(startTimeHours, startTimeMinutes));
              }
              trigger(["startTimeMinutes", "startTimeHours", "startTime", "endTime"]);
            },
          }}
        />
        {RIF(!showingMultipleDayOptions, <span css={{ ...textStyle }}>and expire at</span>)}
        {RIF(
          showingMultipleDayOptions,
          <>
            <span css={{ ...textStyle }}>and expire</span>
            <TaskScheduleSettingsDayIntervalInput
              register={registerName(register, "endTimeDays")}
              failedValidation={!!errors.endTimeDays}
              {...{
                min: {
                  value: 1,
                  message: ScheduleFormValidationErrorMessages.shouldBePositiveNumber("Days"),
                },
              }}
            />
            <span css={{ ...textStyle }}>later at</span>
          </>,
        )}
        <input
          type="hidden"
          {...register("endTime", {
            required: {
              value: true,
              message: ScheduleFormValidationErrorMessages.required("End Time"),
            },
            pattern: {
              value: ScheduleTime.MAY_BE_MORE_THAN_ONE_DAY_REGEX,
              message: ScheduleFormValidationErrorMessages.timeInvalid("End Time"),
            },
            validate: {
              validTimeInterval: (value) => {
                const { success } = ScheduleFormValidator.validateTimeInterval({
                  startTime: getValues("startTime"),
                  endTime: value,
                  endTimeParseOptions: {
                    mayBeMoreThanOneDay: true,
                  },
                });
                return success
                  ? true
                  : ScheduleFormValidationErrorMessages.shouldBeGreaterThan(
                      "End Time",
                      "Start Time",
                    );
              },
            },
            deps: ["startTime"],
          })}
        />
        <TaskScheduleSettingsTimeInput
          control={control}
          hoursName={"endTimeHours"}
          minutesName={"endTimeMinutes"}
          failedValidation={!!errors.endTimeHours || !!errors.endTimeMinutes}
          hoursRules={{
            required: {
              value: true,
              message: ScheduleFormValidationErrorMessages.required("End Time Hours"),
            },
            min: {
              value: 0,
              message: ScheduleFormValidationErrorMessages.hourInvalid("End Time Hours"),
            },
            max: {
              value: 23,
              message: ScheduleFormValidationErrorMessages.hourInvalid("End Time Hours"),
            },
            onChange: (e) => {
              const endTimeHours = parseInt(e.target.value);
              setValue("endTimeHours", endTimeHours);
              const endTimeDays = getValues("endTimeDays");
              const endTimeMinutes = getValues("endTimeMinutes");
              if (!isNil(endTimeMinutes)) {
                setValue(
                  "endTime",
                  ScheduleTimeHelper.format(endTimeHours + (endTimeDays ?? 0) * 24, endTimeMinutes),
                );
              }
              trigger(["endTimeHours", "endTimeMinutes", "endTimeDays", "endTime", "startTime"]);
            },
          }}
          minutesRules={{
            required: {
              value: true,
              message: ScheduleFormValidationErrorMessages.required("End Time Minutes"),
            },
            min: {
              value: 0,
              message: ScheduleFormValidationErrorMessages.minuteInvalid("End Time Minutes"),
            },
            max: {
              value: 59,
              message: ScheduleFormValidationErrorMessages.minuteInvalid("End Time Minutes"),
            },
            onChange: (e) => {
              const endTimeMinutes = parseInt(e.target.value);
              setValue("endTimeMinutes", endTimeMinutes);
              const endTimeDays = getValues("endTimeDays");
              const endTimeHours = getValues("endTimeHours");
              if (!isNil(endTimeHours)) {
                setValue(
                  "endTime",
                  ScheduleTimeHelper.format(endTimeHours + (endTimeDays ?? 0) * 24, endTimeMinutes),
                );
              }
              trigger(["endTimeMinutes", "endTimeHours", "endTimeDays", "endTime", "startTime"]);
            },
          }}
        />
        <TaskScheduleSettingsActionButton
          onClick={() => {
            if (showingMultipleDayOptions) {
              setCachedEndTimeDays(getValues("endTimeDays") ?? 1);
              setValue("endTimeDays", undefined);
              setShowingMultipleDayOptions(false);
              trigger(["endTimeDays", "endTimeHours", "endTimeMinutes", "endTime", "startTime"]);
            } else {
              setValue("endTimeDays", cachedEndTimeDays);
              setShowingMultipleDayOptions(true);
              trigger();
            }
          }}
        >
          {showingMultipleDayOptions ? "Change To Expire On The Same Day" : "Extend Duration"}
        </TaskScheduleSettingsActionButton>
      </div>
      {RIF(latestErrorMessage, (message) => (
        <div css={{ ...textStyle, color: color.warning }}>{message}</div>
      ))}
    </div>
  );
};
